import React, { useEffect, useState } from 'react';

import { TagFilled } from '@ant-design/icons/lib';
import classNames from 'classnames';
import { format } from 'date-fns';
import styled from 'styled-components';

import CommunitySwitcher from 'common/CommunitySwitcherTabs/CommunitySwitcher';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';

import DataTable from './DataTable';
import { buildIncentivesCSVData } from './utils';
import { useSalesBattlecardContext } from '../../../components/SalesBattlecard/SalesBattlecardContext';
import { LIVING_TYPES_LABELS } from '../../../constants/livingTypes';
import { getFilename } from '../GraphWidget/utils';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

interface Props {
  data: any;
}

const StyledDataTable = styled(DataTable)`
  &&&&&& {
    .ant-table-thead > tr > th {
      border-bottom: none;
    }

    .ant-table-tbody > tr:hover > td {
      background: transparent !important;
    }

    .ant-table-tbody > tr > td {
      border: none !important;
    }

    .ant-table-tbody > tr {
      position: relative;
    }

    .ant-table-tbody > tr::after {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid var(--line-gray);
      pointer-events: none;
    }

    .ant-table-tbody > tr:not(:first-child)::after {
      top: -1px;
    }

    width: 100%;
    td {
      padding: 11px;
    }
    th {
      &.ant-table-cell::before {
        background-color: transparent;
      }
    }
  }
`;

const IncentiveCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .anticon {
    margin-top: 3px;
    color: var(--positive-green);
    transform: rotate(-90deg);
    font-size: 14px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DataTableIncentives = ({ data }: Props) => {
  const [selectedCommunityId, setSelectedCommunityId] = useState<number | null>(null);
  const { community, viewFilters, competitors } = useCommunityViewContext();
  const { selectedCompetitorId: battlecardCompetitorId } = useSalesBattlecardContext();
  const { name, setCSVData, setFilename } = useWidgetContext();

  const filteredCompetitors = viewFilters?.competitors?.length
    ? (competitors ?? []).filter((c: any) => viewFilters.competitors.includes(c?.id))
    : (competitors ?? []);

  const effectiveCompetitors = battlecardCompetitorId
    ? filteredCompetitors.filter((c: any) => c?.id == battlecardCompetitorId)
    : filteredCompetitors;

  const communityColumn = {
    title: 'COMMUNITY',
    dataIndex: 'community_name',
    width: '30%',
    align: 'left' as const,
    render: (value: any, record: any) => (
      <span
        className={classNames({
          highlighted: record.community_type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY,
        })}
      >
        {value}
      </span>
    ),
  };
  const columns = [
    ...(selectedCommunityId === null ? [communityColumn] : []),

    {
      title: 'INCENTIVE',
      dataIndex: 'description',
      align: 'left' as const,
      render: (value: any) => (
        <IncentiveCell>
          <TagFilled />
          <span>{value}</span>
        </IncentiveCell>
      ),
    },
    {
      title: 'CARE TYPE',
      dataIndex: 'living_types',
      width: '20%',
      render: (value: any) => (
        <span>
          {value.map((livingType: any, idx: number) => (
            <span key={livingType}>
              {LIVING_TYPES_LABELS[livingType]}
              {idx !== value?.length - 1 ? ', ' : ''}
            </span>
          ))}
        </span>
      ),
    },
    {
      title: 'ADDED',
      dataIndex: 'date',
      width: '12%',
      render: (value: any) => <span>{format(new Date(value), 'MMM dd, yyyy')}</span>,
    },
  ];

  const filteredData = selectedCommunityId ? data?.filter((item: any) => item.community === selectedCommunityId) : data;

  useEffect(() => {
    setSelectedCommunityId(battlecardCompetitorId);
  }, [battlecardCompetitorId]);

  useEffect(() => {
    const exportData = buildIncentivesCSVData(filteredData, selectedCommunityId);
    const selectedCommunity = selectedCommunityId
      ? [community, ...competitors].find((item) => item?.id === selectedCommunityId)
      : community;
    setFilename(getFilename(selectedCommunity?.name || 'Community', name));
    setCSVData(exportData);
  }, [selectedCommunityId]);

  const hasData = filteredData.length > 0;

  return (
    <Container data-testid="incentives-data-table-container">
      <CommunitySwitcher
        dataTestId="incentives-widget"
        selectedCommunityId={selectedCommunityId}
        onCommunityChange={(id: number | null) => setSelectedCommunityId(id)}
        filteredCompetitors={effectiveCompetitors}
      />
      {!hasData && <NoDataInfo data-testid="incentives-table-empty"> No Data Available</NoDataInfo>}
      {hasData && (
        <StyledDataTable
          data-testid="incentives-widget-table"
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          rowKey={(record: any) => record?.id}
        />
      )}
    </Container>
  );
};

export default DataTableIncentives;
